import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save, Send } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { submitPetition } from '../lib/supabase';
import toast from 'react-hot-toast';

const categories = [
  'Mediu și Climă',
  'Drepturile Muncii',
  'Sănătate Publică',
  'Educație',
  'Justiție Socială',
  'Politici Europene',
  'Economie și Finanțe',
  'Migrație și Azil',
  'Tehnologie și Digitalizare',
  'Cultură și Arte',
  'Altele'
];

const SubmitPetition = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      content: '',
      category: '',
      target_signatures: 1000,
      submitted_by_name: '',
      submitted_by_email: ''
    }
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await submitPetition(data);
      toast.success('Petiția a fost trimisă pentru aprobare!');
      navigate('/');
    } catch (error) {
      toast.error('Eroare la trimiterea petiției');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom color="primary">
          Creează o Petiție
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Completează formularul pentru a propune o petiție. Toate petițiile sunt revizuite înainte de publicare.
        </Typography>
      </Box>

      <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2">
            <strong>Proces de aprobare:</strong> Petiția ta va fi revizuită de echipa PSDE în maxim 48 de ore. 
            Vei fi notificat prin email despre statusul aprobarării.
          </Typography>
        </Alert>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* Informații personale */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Informațiile tale
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="submitted_by_name"
                control={control}
                rules={{ 
                  required: 'Numele este obligatoriu',
                  minLength: { value: 2, message: 'Numele trebuie să aibă cel puțin 2 caractere' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Numele tău complet"
                    error={!!errors.submitted_by_name}
                    helperText={errors.submitted_by_name?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="submitted_by_email"
                control={control}
                rules={{ 
                  required: 'Email-ul este obligatoriu',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email invalid'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="email"
                    label="Adresa ta de email"
                    error={!!errors.submitted_by_email}
                    helperText={errors.submitted_by_email?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            {/* Detaliile petiției */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                Detaliile petiției
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="title"
                control={control}
                rules={{ 
                  required: 'Titlul este obligatoriu',
                  minLength: { value: 10, message: 'Titlul trebuie să aibă cel puțin 10 caractere' },
                  maxLength: { value: 200, message: 'Titlul nu poate depăși 200 de caractere' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Titlul Petiției"
                    placeholder="Introduceți un titlu clar și concis..."
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                rules={{ 
                  required: 'Descrierea este obligatorie',
                  minLength: { value: 50, message: 'Descrierea trebuie să aibă cel puțin 50 de caractere' },
                  maxLength: { value: 500, message: 'Descrierea nu poate depăși 500 de caractere' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label="Descriere Scurtă"
                    placeholder="O descriere scurtă care va apărea pe lista de petiții..."
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="content"
                control={control}
                rules={{ 
                  required: 'Conținutul este obligatoriu',
                  minLength: { value: 100, message: 'Conținutul trebuie să aibă cel puțin 100 de caractere' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={8}
                    label="Conținutul Complet al Petiției"
                    placeholder="Descrierea detaliată a petiției, motivele pentru care este importantă, ce se dorește să se realizeze..."
                    error={!!errors.content}
                    helperText={errors.content?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      {...field}
                      label="Categoria"
                      disabled={loading}
                    >
                      <MenuItem value="">
                        <em>Selectează o categorie</em>
                      </MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="target_signatures"
                control={control}
                rules={{ 
                  required: 'Obiectivul de semnături este obligatoriu',
                  min: { value: 100, message: 'Obiectivul trebuie să fie cel puțin 100' },
                  max: { value: 1000000, message: 'Obiectivul nu poate depăși 1,000,000' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Obiectiv Semnături"
                    placeholder="1000"
                    error={!!errors.target_signatures}
                    helperText={errors.target_signatures?.message}
                    disabled={loading}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Notă:</strong> Petiția ta va fi revizuită pentru a se asigura că respectă 
                  valorile și principiile PSDE. Ne rezervăm dreptul de a edita sau respinge petițiile 
                  care nu îndeplinesc standardele noastre.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                  disabled={loading}
                >
                  Anulează
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  disabled={loading}
                  size="large"
                >
                  {loading ? 'Se trimite...' : 'Trimite pentru Aprobare'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default SubmitPetition;
